import React, { useContext, useEffect, useMemo, useState, } from "react";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { PLACEHOLDER_MOVIE_POSTER } from "@config/ImageSizes";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { SearchContext } from "@context/SearchContext";
import Loader from "@components/Ui/Loader";
import DateSelector from "@components/V3/DateSelector";
import FilteredExperience from "@components/V3/Experiences/filteredExperience";
import SelectExperienceSessions from "@components/V3/Experiences/selectExperience";
import { getAllExperiences, getExperienceDetails, } from "@utils/experienceUtils";
import { requestUserLocation } from "@utils/locationUtils";
import { groupSessionsByVenueWithMovies } from "@utils/sessionGroupingUtils";
import { filterSessionsByDateAndExperience, getUniqueSessionDates, } from "@utils/sessionUtils";
import "./style.scss";

const Experiences = ({ defaultSearchDistance = 15 }) => {
    const location = useLocation();
    const { userCoords, setUserCoords } = useContext(AuthContext);
    const { cdnUrl } = useContext(ApplicationContext);
    const { sessions, fetchMovieSessions, setMovie, setSessions, isLoadingSessions, } = useContext(SearchContext);
    const fallbackImage = `${cdnUrl}${PLACEHOLDER_MOVIE_POSTER}`;
    const [groupedVenues, setGroupedVenues] = useState([]);
    const [selection, setSelection] = useState({ date: "", experience: "" });
    const [locationReady, setLocationReady] = useState(false);
    const [isGrouping, setIsGrouping] = useState(true);
    const [hasGroupedOnce, setHasGroupedOnce] = useState(false);
    const currentPath = location.pathname;
    const isMainExperiencesPage = currentPath === "/experiences";
    const slug = !isMainExperiencesPage ? currentPath.split("/")[2]?.toLowerCase() : null;
    const allExperiences = getAllExperiences();
    const experienceMatch = slug ? allExperiences.find((exp) => exp.path === slug) : null;
    const experienceCode = experienceMatch?.code || null;
    const bannerImage = experienceMatch?.banner || "experiences-banner.jpg";
    const experienceLabel = experienceMatch?.label || "";
    const uniqueDates = useMemo(() => getUniqueSessionDates(sessions), [sessions]);

    useEffect(() => {
        if (!userCoords?.latitude || !userCoords?.longitude) {
            requestUserLocation((coords) => {
                if (coords) setUserCoords(coords);
                setLocationReady(true);
            });
        } else {
            setLocationReady(true);
        }
    }, [userCoords]);

    useEffect(() => {
        if (!experienceCode || !locationReady) return;

        const ticketClassId = getExperienceDetails(experienceCode, { get: "ticketClassId", });
        if (!ticketClassId) return;

        setMovie(null);
        setSelection({ date: "", experience: experienceCode });
        setGroupedVenues([]);
        setSessions([]);
        setHasGroupedOnce(false);

        const params = {
            ticket_class_id: ticketClassId,
            search_distance: defaultSearchDistance,
        };

        if (userCoords?.latitude && userCoords?.longitude) {
            params.lat = userCoords.latitude;
            params.lng = userCoords.longitude;
        }
        fetchMovieSessions(params);
    }, [experienceCode, locationReady, userCoords]);


    useEffect(() => {
        if (!selection.experience || sessions.length === 0) {
            if (locationReady) {
                setHasGroupedOnce(true);
            }
            setIsGrouping(false);
            return;
        }

        if (!selection.date) {
            const validSessions = filterSessionsByDateAndExperience(sessions, "", selection.experience
            );
            const availableDates = getUniqueSessionDates(validSessions);
            const firstDate = availableDates[0];

            if (firstDate) {
                setSelection((prev) => ({ ...prev, date: firstDate }));
                return;
            }

            setIsGrouping(false);
            setHasGroupedOnce(true);
            return;
        }

        setIsGrouping(true);

        const filtered = filterSessionsByDateAndExperience(
            sessions,
            selection.date,
            selection.experience
        );
        const grouped = filtered.reduce(groupSessionsByVenueWithMovies, []);

        grouped.forEach((venue) => {
            if (venue.sessionsByMovie) {
                Object.values(venue.sessionsByMovie).forEach((movieGroup) => {
                    if (Array.isArray(movieGroup.sessions)) {
                        movieGroup.sessions.sort(
                            (a, b) =>
                                new Date(a.session_date) -
                                new Date(b.session_date)
                        );
                    }
                });
            }
        });

        grouped.sort((a, b) => a.venue_distance - b.venue_distance);
        setGroupedVenues(grouped);
        setIsGrouping(false);
        setHasGroupedOnce(true);
    }, [sessions, selection.experience, selection.date]);


    return (
        <div className="experiences">
            <div className="experiences__wrapper">
                <picture className="experiences__picture">
                    <source
                        media="(min-width: 768px)"
                        srcSet={`${cdnUrl}asset/img/${bannerImage}`}
                    />
                    <img
                        className="experiences__image"
                        src={`${cdnUrl}asset/img/${bannerImage}`}
                        alt="Experiences Image"
                    />
                </picture>

                {!isMainExperiencesPage && (
                    <>
                        <div className="experiences__text-container">
                            <h1 className="experiences__title">Experiences</h1>
                            <p className="experiences__text">
                                {experienceLabel}
                            </p>
                        </div>
                        <div
                            className={`experiences__triangle experiences__triangle--${experienceCode}`}
                        />
                    </>
                )}
            </div>

            {isMainExperiencesPage ? (
                <>
                    <div className="experiences__intro">
                        <h1 className="experiences__main-title">
                            Experiences
                        </h1>
                        <p className="paragraph">
                            Discover the cinema experience that fits your vibe —
                            with our fabulous cinema partners bringing you
                            everything from luxe Gold Class to cosy Standard,
                            and all points in between.
                        </p>
                    </div>
                    <SelectExperienceSessions
                        experience={selection.experience}
                        setExperience={(exp) =>
                            setSelection((prev) => ({
                                ...prev,
                                experience: exp,
                            }))
                        }
                    />
                </>
            ) : (
                <div className="experiences__main-list">
                    <DateSelector
                        uniqueDates={uniqueDates}
                        handleClickDate={(date) =>
                            setSelection((prev) => ({ ...prev, date }))
                        }
                        selectedDate={selection.date}
                    />

                    {isLoadingSessions || isGrouping ? (
                        <Loader isLoading text="Fetching Movies" />
                    ) : groupedVenues.length > 0 ? (
                        <FilteredExperience
                            groupedVenues={groupedVenues}
                            selection={selection}
                            onImageError={(e) =>
                                (e.target.src = fallbackImage)
                            }
                        />
                    ) : hasGroupedOnce ? (
                        <div className="experiences__message">
                            <p className="experiences__message-title">
                                Sorry, we couldn&apos;t find any results for{" "}
                                {experienceLabel}
                                {selection.date && (
                                    <>
                                        {" "}
                                        on{" "}
                                        {format(
                                            new Date(selection.date),
                                            "EEE d MMM"
                                        )}
                                    </>
                                )}
                            </p>
                            <p className="experiences__message-text">
                                Please select another experience type, or select
                                a different date
                            </p>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default Experiences;
